"use client";
import React, { useEffect, useState, useCallback, useRef } from "react";
import IMAGES from "../../data/assetData";
import { useForm } from "react-hook-form";
import { usefetchDataFromApi } from "../../utils/api";
import Image from "next/image";
import Button from "@/components/ui/Button";
import { useDispatch, useSelector } from "react-redux";
import { getApiToken } from "@/redux/config/configSlice";
import { getUserDetails } from "@/redux/user/userSlice";
import useScrollLock, { useIsScrollLocked } from "@/hooks/useScrollLock";
import { readSearchIntent, isLeadPopupSuppressed, suppressLeadPopup, describeIntent, leadIntentFields } from "@/utils/leadIntent";
import { trackLeadPopup } from "@/utils/analytics";

// How long it waits after a blocking overlay clears before taking the screen —
// long enough that it reads as a new thing arriving rather than a flicker of the
// sheet the user just dismissed.
const OPEN_AFTER_UNBLOCK_MS = 800;

// Touch devices have no exit intent, and they are ~90% of this traffic. A
// decisive scroll back toward the top is the gesture that precedes leaving;
// this much stillness is the backstop for everyone who just stops.
const SCROLL_UP_PX = 120;
const IDLE_MS = 45000;

const getFocusableElements = (rootEl) => {
  if (!rootEl) return [];
  const selector =
    'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

  return Array.from(rootEl.querySelectorAll(selector)).filter((el) => {
    const isHidden = el.getAttribute("aria-hidden") === "true";
    return !isHidden && (el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement);
  });
};

// Reusable Close Icon (accessible)
const CloseIcon = ({ onClick }) => (
  <button
    type="button"
    className="absolute right-4 top-4 z-10 rounded-full p-2 text-white/90 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-2 focus-visible:ring-offset-black/40"
    onClick={onClick}
    aria-label="Close lead popup"
  >
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-6">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  </button>
);

const UserLeadPopup = ({ context = "" }) => {
  const [open, setOpen] = useState(false);
  // "armed" = the delay has elapsed and the popup wants the screen. It only
  // becomes "open" once nothing else is holding it (see below), so the timer
  // firing at a bad moment postpones the popup instead of losing it.
  const [armed, setArmed] = useState(false);
  const [showMsg, setShowMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("phone"); // "phone" | "otp" | "success"
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [smsLoading, setSmsLoading] = useState(false);

  const dispatch = useDispatch();
  const overlayAlreadyOpen = useIsScrollLocked();
  const isAuthenticated = useSelector((state) => state.user.isAuthenticated);
  const totalCartCount = useSelector((state) => state.order.totalCartCount);
  // On the results page this component mounts BEFORE the search settles, so
  // without this the intent written by rideContainer would land too late and
  // the effect below would never look again.
  const resultCount = useSelector((state) => state.search?.searchVehicles?.length || 0);

  // What they were actually looking at, so the agent does not open cold. Read
  // at the moment it is needed rather than cached: no ref, no stale copy, and
  // if the search has aged out by then the page's own label still stands.
  const readContext = useCallback(() => describeIntent(readSearchIntent()) || context, [context]);

  const dialogRef = useRef(null);
  const phoneInputRef = useRef(null);
  const otpInputRef = useRef(null);
  const lastActiveElementRef = useRef(null);

  useScrollLock(open);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: { countrycode: "+91" },
  });

  const closeModal = useCallback(() => {
    suppressLeadPopup(false);
    trackLeadPopup("dismissed", readContext());
    setOpen(false);
    setArmed(false);
    setStep("phone");
    setShowMsg("");
    setOtp("");
    setSmsLoading(false);
  }, [readContext]);

  // Handle phone number submission
  const onSubmit = useCallback(async (data) => {
    const { phoneNumber: phone } = data;
    setPhoneNumber(phone);
    setShowMsg("");
    setLoading(true);

    try {
      const response = await usefetchDataFromApi({
        url: "ticket/submit-lead",
        method: "POST",
        data: {
          mobile: phone,
          details: readContext(),
          ...leadIntentFields(),
          platform: 3,
        },
      });

      if (response.success) {
        if (response.data?.requiresOtp) {
          // Show OTP step
          setStep("otp");
          setShowMsg(response.data?.message || "OTP sent to your mobile");
        } else {
          // User already verified - show success
          setShowMsg(response.data?.message || "Thank you! We'll contact you soon.");
          suppressLeadPopup(true);
          trackLeadPopup("submitted", readContext());
          setTimeout(() => {
            setOpen(false);
            setArmed(false);
          }, 2000);
        }
      } else {
        setShowMsg(response.data?.error || "Submission failed.");
      }
    } catch (error) {
      setShowMsg("Something went wrong!");
    } finally {
      setLoading(false);
    }
  }, [readContext]);

  // Handle OTP verification
  const handleOtpVerify = useCallback(async () => {
    if (!otp || otp.length !== 4) {
      setShowMsg("Please enter a valid 4-digit OTP");
      return;
    }

    setShowMsg("");
    setLoading(true);

    try {
      const response = await usefetchDataFromApi({
        url: "ticket/verify-lead-otp",
        method: "POST",
        data: {
          mobile: phoneNumber,
          otp: otp,
          details: readContext(),
          platform: 3,
        },
      });

      if (response.success) {
        // OTP verified => backend returns the standard login payload. Persist it
        // exactly like the login flow (Login/index.jsx) so this lead is now an
        // authenticated session and every subsequent search carries their userId.
        const respData = response.data?.data;
        if (respData?.token) {
          dispatch(
            getUserDetails({
              userDetails: { ...respData, userType: respData.type },
              isAuthenticated: true,
            })
          );
          dispatch(getApiToken(respData.token));
        }
        setShowMsg(response.data?.message || "Verified! We'll contact you soon.");
        suppressLeadPopup(true);
        trackLeadPopup("submitted", readContext());
        setTimeout(() => setOpen(false), 2000);
      } else {
        setShowMsg(response.message || "Invalid OTP. Please try again.");
      }
    } catch (error) {
      setShowMsg("Something went wrong!");
    } finally {
      setLoading(false);
    }
  }, [phoneNumber, otp, dispatch, readContext]);

  // The delivery webhook rescues an OTP Meta REJECTS, but a message it accepts
  // and never delivers produces no callback at all - this is the only way out of
  // that one, and it also covers simply not noticing the WhatsApp.
  const handleSmsResend = useCallback(async () => {
    setShowMsg("");
    setSmsLoading(true);

    try {
      const response = await usefetchDataFromApi({
        url: "ticket/resend-lead-otp-sms",
        method: "POST",
        data: { mobile: phoneNumber, platform: 3 },
      });

      setShowMsg(
        response.success
          ? response.data?.message || "OTP sent by SMS."
          : response.data?.error || "Could not send the SMS just now."
      );
    } catch (error) {
      setShowMsg("Something went wrong!");
    } finally {
      setSmsLoading(false);
    }
  }, [phoneNumber]);

  // Handle skip OTP
  const handleSkipOtp = useCallback(async () => {
    setShowMsg("");
    setLoading(true);

    try {
      const response = await usefetchDataFromApi({
        url: "ticket/submit-lead-guest",
        method: "POST",
        data: {
          mobile: phoneNumber,
          details: readContext(),
          ...leadIntentFields(),
          platform: 3,
        },
      });

      if (response.success) {
        setShowMsg(response.data?.message || "Thank you! We'll contact you soon.");
        suppressLeadPopup(true);
        trackLeadPopup("submitted", readContext());
        setTimeout(() => setOpen(false), 2000);
      } else {
        setShowMsg(response.message || "Submission failed.");
      }
    } catch (error) {
      setShowMsg("Something went wrong!");
    } finally {
      setLoading(false);
    }
  }, [phoneNumber, readContext]);

  useEffect(() => {
    // Everyone who has nothing to gain from being asked, or whom we have no
    // business interrupting. A signed-in customer's number is already on file,
    // so asking for it can only annoy; someone holding a cart is mid-booking
    // and belongs to the cart-recovery stream, not to a cold callback.
    if (isAuthenticated || (totalCartCount || 0) > 0 || isLeadPopupSuppressed()) return undefined;

    // The gate that replaced a 5-second timer. Over 16 Aug - 15 Sep the timer
    // caught 1,512 people who had never run a search: 3 paid bookings between
    // them. The 175 who had searched and seen vehicles converted at 5.14%.
    // It also keeps the popup off a page someone just landed on from Google,
    // which is the pattern the intrusive-interstitial guidance is about.
    const searchIntent = readSearchIntent();
    if (!searchIntent) return undefined;

    // Ask on the way out, not on the way in.
    let idleId;
    // Measured against the DEEPEST point reached, not the previous event. A
    // momentum flick up fires dozens of small scroll events (~30px each), so a
    // consecutive-event delta never reaches the threshold and the trigger simply
    // never fired on touch — which is ~90% of this traffic. Verified in a real
    // browser: an animated 900px scroll-up emitted 28 events and armed nothing,
    // while a single-event 900px jump armed correctly.
    let peakY = window.scrollY;
    const arm = () => setArmed(true);

    const onMouseOut = (e) => {
      if (!e.relatedTarget && e.clientY <= 0) arm();
    };

    const onScroll = () => {
      const y = window.scrollY;
      if (y > peakY) {
        peakY = y;
      } else if (peakY - y > SCROLL_UP_PX && y < 400) {
        arm();
      }
      window.clearTimeout(idleId);
      idleId = window.setTimeout(arm, IDLE_MS);
    };

    idleId = window.setTimeout(arm, IDLE_MS);
    document.addEventListener("mouseout", onMouseOut);
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      document.removeEventListener("mouseout", onMouseOut);
      window.removeEventListener("scroll", onScroll);
      window.clearTimeout(idleId);
    };
  }, [isAuthenticated, totalCartCount, resultCount]);

  // Never take the screen while something else already owns it. The gesture that
  // arms the popup is very often the one that opens the mobile search sheet, so
  // without this it materialises *behind* the sheet: invisible at z-50, but still
  // stealing focus into its phone field and trapping Tab. Staying armed means the
  // lead is captured a moment later instead of lost.
  useEffect(() => {
    if (!armed || open || overlayAlreadyOpen) return undefined;
    // Re-checked here, not just on mount: the cart count arrives asynchronously,
    // so a customer who already had a cart could otherwise be armed before we knew.
    if (isAuthenticated || (totalCartCount || 0) > 0) return undefined;
    const id = window.setTimeout(() => setOpen(true), OPEN_AFTER_UNBLOCK_MS);
    return () => window.clearTimeout(id);
  }, [armed, open, overlayAlreadyOpen, isAuthenticated, totalCartCount]);

  // Manage focus and keyboard handling (Esc/Tab) when modal is open. The body
  // scroll lock is the shared, reference-counted one (useScrollLock above) —
  // this component used to save and restore `body.style.overflow` itself, which
  // meant that if it opened while another overlay held a lock it recorded
  // "hidden" as the page's resting state and wrote it back on close, leaving
  // the page permanently unscrollable.
  useEffect(() => {
    if (!open) return;

    trackLeadPopup("shown", readContext());
    lastActiveElementRef.current = document.activeElement;

    const focusTarget = step === "otp" ? otpInputRef.current : phoneInputRef.current;
    requestAnimationFrame(() => focusTarget?.focus());

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeModal();
        return;
      }

      if (e.key !== "Tab") return;
      const root = dialogRef.current;
      const focusables = getFocusableElements(root);
      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const isShift = e.shiftKey;
      const active = document.activeElement;
      const isInside = root?.contains(active);

      // If focus somehow escaped the dialog, bring it back in.
      if (!isInside) {
        e.preventDefault();
        (isShift ? last : first).focus();
        return;
      }

      if (!isShift && active === last) {
        e.preventDefault();
        first.focus();
      } else if (isShift && active === first) {
        e.preventDefault();
        last.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      const lastActive = lastActiveElementRef.current;
      if (lastActive && typeof lastActive.focus === "function") {
        requestAnimationFrame(() => lastActive.focus());
      }
    };
  }, [open, step, closeModal, readContext]);

  return (
    <>
      {/* z-[1000], not z-50. At 50 this modal sat UNDER MobileBar (51/52),
          NavigationBar (100), the WhatsApp FAB (999) and SmartAppBanner (99999)
          — all of which drew on top of a scrim meant to cover the page. 1000 is
          the app's modal band (the search sheet's scrim uses the same), and the
          two root-stacking-context floaters are hidden by
          `html[data-scroll-locked]` in globals.css while any modal is up. */}
      {open && (
        <div
          className="fixed inset-0 z-[1000] bg-black/50 overflow-y-auto overscroll-contain"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div className="min-h-full flex items-center justify-center p-4 sm:p-6">
            <div
              ref={dialogRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby="lead-popup-title"
              className="relative w-full max-w-lg rounded-2xl bg-lead-popup bg-cover bg-center font-poppins text-center shadow-xl max-h-[calc(100vh-2rem)] overflow-auto"
            >
              <CloseIcon onClick={closeModal} />
              <div className="p-4 lg:p-6">
                <div className="text-lg lg:text-2xl text-white font-medium mb-2 mt-4 lg:mt-0">
                  Price slashed by <span className="text-primary font-bold [WebkitTextStroke:0.5px_#fff]">30%</span>
                </div>
                <Image src={IMAGES.SummerEs} width={70} height={70} alt="Promo" className="m-auto" style={{ width: 70, height: 70, objectFit: "contain" }} />
                <h3 id="lead-popup-title" className="font-extrabold text-base lg:text-lg text-white mt-2 mb-2">
                  Book your self-drive bike or car Now.
                </h3>
                {step === "phone" && (
                  <form onSubmit={handleSubmit(onSubmit)} className="relative">
                    <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-center gap-3 lg:gap-6">
	                      <div className="bg-white px-4 py-2 rounded-full flex items-center gap-3 h-[44px] w-full lg:w-1/2">
                        <div className="w-[50px] text-base text-gray-400 leading-[30px] shrink-0">+91</div>
                        {(() => {
                          const phoneRegister = register("phoneNumber", {
                            required: "Phone number is required",
                            pattern: {
                              value: /^[0-9]{10}$/,
                              message: "Enter a valid 10-digit number",
                            },
                          });

                          return (
                            <input
                              {...phoneRegister}
                              ref={(el) => {
                                phoneRegister.ref(el);
                                phoneInputRef.current = el;
                              }}
                              type="tel"
                              inputMode="numeric"
                              autoComplete="tel"
                              aria-label="Phone number"
                              aria-invalid={Boolean(errors.phoneNumber)}
                              aria-describedby={errors.phoneNumber ? "lead-phone-error" : undefined}
                              placeholder="Enter your number"
                              className="flex-1 text-base text-black w-full bg-transparent focus-visible:outline-none"
                              maxLength={10}
                              onChange={(e) => {
                                e.target.value = e.target.value.replace(/\D/g, "");
                                phoneRegister.onChange(e);
                              }}
                            />
                          );
                        })()}
                      </div>
                      <Button type="submit" size="md" loading={loading}>
                        {loading ? "Submitting..." : "Get a Call Back"}
                      </Button>
                    </div>
                    {errors.phoneNumber && (
                      <p id="lead-phone-error" className="mt-2 text-left text-yellow-200 text-sm">
                        {errors.phoneNumber.message}
                      </p>
                    )}
                  </form>
                )}

                {step === "otp" && (
                  <div className="relative">
                    <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-center gap-3 lg:gap-4">
	                      <div className="bg-white px-4 py-2 rounded-full flex items-center h-[44px] w-full lg:w-1/2">
                        <input
                          ref={otpInputRef}
                          type="tel"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          aria-label="OTP"
                          placeholder="Enter 4-digit OTP"
                          className="flex-1 text-base text-black w-full bg-transparent text-center focus-visible:outline-none"
                          maxLength={4}
                          value={otp}
                          onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                        />
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Button size="md" onClick={handleOtpVerify} loading={loading}>
                          {loading ? "Verifying..." : "Verify"}
                        </Button>
                        <Button variant="ghost" size="md" onClick={handleSkipOtp} disabled={loading}>
                          Skip
                        </Button>
                      </div>
                    </div>
                    <p className="text-white/90 text-sm mt-2 break-words">Sent on WhatsApp to +91-{phoneNumber}</p>
                    <button
                      type="button"
                      onClick={handleSmsResend}
                      disabled={smsLoading || loading}
                      className="mx-auto inline-flex min-h-[44px] items-center justify-center rounded px-2 text-sm text-white/90 underline underline-offset-4 hover:text-white disabled:no-underline disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
                    >
                      {smsLoading ? "Sending SMS\u2026" : "Didn\u2019t get it? Send by SMS"}
                    </button>
                  </div>
                )}

                {showMsg && (
                  <p className="mt-4 rounded-lg bg-black/30 px-3 py-2 text-white text-sm lg:text-base" role="status" aria-live="polite">
                    {showMsg}
                  </p>
                )}

                <ul className="mt-6 lg:mt-10 py-4 lg:py-5 flex gap-3 text-sm lg:text-base text-white font-extrabold justify-between">
                  <li>10,000+ <br /> Top Rated Vehicles</li>
                  <li>Top Rated <br /> Verified Partners</li>
                  <li>Seamless <br /> Booking Experience</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
export default UserLeadPopup;
